// ============================================
// Form Wizard Component
// Multi-step form with validation -> Generate PDFs -> Preview -> Submit
// ============================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { DocumentGroupKey, DocumentKey, FormSubmission, FormWizardStep, FormStep, getDefaultCYECMAssessmentData, getTestCYECMAssessmentData } from '../types';
import { getTestAdultEcmFlatMap } from '../utils/testAdultEcmData';
import { getTestCyEcmFlatMap } from '../utils/testCyEcmData';
import { formsAPI, localStorageAPI, GeneratedPDF } from '../services/api';
import MemberInfoSection from './sections/MemberInfoSection';
import ROISection from './sections/ROISection';
import HousingSection from './sections/HousingSection';
import ConsentSection from './sections/ConsentSection';
import CarePlanSection from './sections/CarePlanSection';
import PartnershipSection from './sections/PartnershipSection';
import PartnershipCarePlanSection from './sections/PartnershipCarePlanSection';
import IHSPSection from './sections/IHSPSection';
import PhpCsReferralSection from './sections/PhpCsReferralSection';
import EcmReferralChildYouthSection from './sections/EcmReferralChildYouthSection';
import EcmReferralAdultSection from './sections/EcmReferralAdultSection';
import CchpEcmReferralChildYouthSection from './sections/CchpEcmReferralChildYouthSection';
import CchpEcmReferralAdultSection from './sections/CchpEcmReferralAdultSection';
import CYECMAssessmentSection from './sections/CYECMAssessmentSection';
import AdultECMAssessmentSection from './sections/AdultECMAssessmentSection';
import DocumentSelectionSection, { DOCUMENT_LABELS } from './sections/DocumentSelectionSection';
import SignatureSection from './sections/SignatureSection';
import ProgressBar from './ui/ProgressBar';
import PDFPreview from './PDFPreview';
import SubmissionSuccess from './SubmissionSuccess';
import { mapCyEcmAssessmentToFlat } from './pdfMappingHelper';
import ShareModal from './ShareModal';
import { getDashboardToken, isAdminDashboardUser, getDashboardUser } from '../utils/session';
import { useLanguage } from '../i18n/LanguageContext';
import { useViewState } from '../contexts/ViewStateContext';

// Maps wizard step ids (that aren't PDF/form names) to i18n keys.
// PDF/form step ids (consent, care-plan, etc.) are intentionally omitted
// so their titles stay in English.
const STEP_TITLE_KEY: Record<string, string> = {
  'document-selection': 'step.documentSelection.title',
  'member-info': 'step.memberInfo.title',
  signature: 'step.signature.title',
};
const STEP_DESC_KEY: Record<string, string> = {
  'document-selection': 'step.documentSelection.description',
  'member-info': 'step.memberInfo.description',
  signature: 'step.signature.description',
};

const GROUP_DOCUMENTS: Record<DocumentGroupKey, DocumentKey[]> = {
  contra_costa_adult: ['consent', 'ecmRoi', 'adultEcmAssessment'],
  contra_costa_children: ['consent', 'ecmRoi', 'cyEcmAssessment'],
  partnership_adult: ['csConsentPartnership', 'ecmRoi'],
  partnership_children: ['csConsentPartnership', 'ecmRoi'],
};

const DOCUMENT_STEP_ORDER: DocumentKey[] = [
  'consent',
  'csConsentPartnership',
  'ecmRoi',
  'adultEcmAssessment',
  'cyEcmAssessment',
  'housingAssessment',
  'carePlan',
  'partnershipCarePlan',
  'ihsp',
  'phpCsReferral',
  'ecmReferralChildYouth',
  'ecmReferralAdult',
  'cchpEcmReferralChildYouth',
  'cchpEcmReferralAdult',
];

// Function to get localized step titles and descriptions
const getLocalizedDocumentToStep = (language: 'en' | 'es'): Record<DocumentKey, FormWizardStep> => ({
  consent: { 
    id: 'consent', 
    title: language === 'es' ? 'Consentimiento' : 'Consent', 
    description: language === 'es' ? 'Consentimiento' : 'Consent', 
    isComplete: false 
  },
  csConsentPartnership: { 
    id: 'partnership', 
    title: language === 'es' ? 'Consentimiento de Asociación' : 'Consent Partnership', 
    description: language === 'es' ? 'Consentimiento de Asociación' : 'Consent Partnership', 
    isComplete: false 
  },
  ecmRoi: { 
    id: 'roi', 
    title: language === 'es' ? 'Liberación de Información' : 'Release of Information', 
    description: language === 'es' ? 'Liberación de Información' : 'Release of Information', 
    isComplete: false 
  },
  adultEcmAssessment: {
    id: 'adult-ecm-assessment',
    title: language === 'es' ? '(ECM) Evaluación Integral Adulta Comprensiva' : '(ECM) Adult Comprehensive Assessment',
    description: language === 'es' ? 'Evaluación' : 'Assessment',
    isComplete: false,
  },
  cyEcmAssessment: {
    id: 'cy-ecm-assessment',
    title: language === 'es' ? 'Evaluación (Niños)' : 'Assessment (Children)',
    description: language === 'es' ? 'Evaluación' : 'Assessment',
    isComplete: false,
  },
  housingAssessment: { 
    id: 'housing', 
    title: language === 'es' ? 'Evaluación de Vivienda' : 'Housing Assessment', 
    description: language === 'es' ? 'Evaluación de Vivienda' : 'Housing Assessment', 
    isComplete: false 
  },
  carePlan: {
    id: 'care-plan',
    title: language === 'es' ? 'Plan de Atención' : 'Care Plan',
    description: language === 'es' ? 'Plan de Atención' : 'Care Plan',
    isComplete: false
  },
  partnershipCarePlan: {
    id: 'partnership-care-plan',
    title: 'Partnership Care Plan',
    description: 'Partnership Care Plan',
    isComplete: false,
  },
  ihsp: {
    id: 'ihsp',
    title: language === 'es' ? 'Plan de Apoyo de Vivienda' : 'Housing Support Plan',
    description: language === 'es' ? 'Plan de Apoyo de Vivienda' : 'Housing Support Plan',
    isComplete: false,
  },
  phpCsReferral: {
    id: 'php-cs-referral',
    title: 'Community Support Services Referral Form',
    description: 'Community Support Services Referral Form',
    isComplete: false,
  },
  ecmReferralChildYouth: {
    id: 'ecm-referral-child-youth',
    title: 'Enhanced Care Management (ECM) Referral Form for Children/Youth',
    description: 'Enhanced Care Management (ECM) Referral Form for Children/Youth',
    isComplete: false,
  },
  ecmReferralAdult: {
    id: 'ecm-referral-adult',
    title: 'Enhanced Care Management (ECM) Referral Form for Adults',
    description: 'Enhanced Care Management (ECM) Referral Form for Adults',
    isComplete: false,
  },
  cchpEcmReferralChildYouth: {
    id: 'cchp-ecm-referral-child-youth',
    title: 'Contra Costa Health Plan Child/Youth ECM Referral Form',
    description: 'Contra Costa Health Plan Child/Youth ECM Referral Form',
    isComplete: false,
  },
  cchpEcmReferralAdult: {
    id: 'cchp-ecm-referral-adult',
    title: 'Contra Costa Health Plan Adult ECM Referral Form',
    description: 'Contra Costa Health Plan Adult ECM Referral Form',
    isComplete: false,
  },
});

const getRootFieldToStepId = (rootField: string): string => {
  switch (rootField) {
    case 'memberInfo':
      return 'member-info';
    case 'roi':
      return 'roi';
    case 'housing':
      return 'housing';
    case 'consent':
      return 'consent';
    case 'carePlan':
      return 'care-plan';
    case 'partnershipConsent':
      return 'partnership';
    case 'partnershipCarePlan':
      return 'partnership-care-plan';
    case 'ihsp_flat':
      return 'ihsp';
    case 'phpCsReferral_flat':
      return 'php-cs-referral';
    case 'ecmReferralChildYouth_flat':
      return 'ecm-referral-child-youth';
    case 'ecmReferralAdult_flat':
      return 'ecm-referral-adult';
    case 'cchpEcmReferralChildYouth_flat':
      return 'cchp-ecm-referral-child-youth';
    case 'cchpEcmReferralAdult_flat':
      return 'cchp-ecm-referral-adult';
    case 'cyEcmAssessment':
    case 'cyEcmAssessment_flat':
      return 'cy-ecm-assessment';
    case 'adultEcmAssessment_flat':
      return 'adult-ecm-assessment';
    case 'signature':
      return 'signature';
    case 'documentSelection':
    case 'selectedDocuments':
      return 'document-selection';
    default:
      return 'member-info';
  }
};

const isCheckboxInput = (el: Element): el is HTMLInputElement =>
  el.tagName === 'INPUT' && ((el as HTMLInputElement).type || '').toLowerCase() === 'checkbox';

const toDateInputValue = (value?: string | Date | null): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().split('T')[0];
  }

  return Number.isNaN(value.getTime()) ? '' : value.toISOString().split('T')[0];
};

const getSelectedDocKeysFromParam = (selectedPDFsParam: string | null): DocumentKey[] => {
  if (!selectedPDFsParam) {
    return [];
  }

  const formNameToDocKey: Record<string, DocumentKey> = {
    'Consent': 'consent',
    'Consent Partnership': 'csConsentPartnership',
    'ECM Release of Information': 'ecmRoi',
    'Assessment (Children)': 'cyEcmAssessment',
    '(ECM) Adult Comprehensive Assessment': 'adultEcmAssessment',
    'Housing Assessment': 'housingAssessment',
    'Care Plan': 'carePlan',
    'Partnership Care Plan': 'partnershipCarePlan',
    'Housing Support Plan (IHSP)': 'ihsp',
    'Community Support Services Referral Form': 'phpCsReferral',
    'Enhanced Care Management (ECM) Referral Form for Children/Youth': 'ecmReferralChildYouth',
    'Enhanced Care Management (ECM) Referral Form for Adults': 'ecmReferralAdult',
    'Contra Costa Health Plan Child/Youth Enhanced Care Management (ECM) Referral Form': 'cchpEcmReferralChildYouth',
    'Contra Costa Health Plan Adult Enhanced Care Management (ECM) Referral Form': 'cchpEcmReferralAdult',
  };

  const selectedDocKeys = selectedPDFsParam
    .split(',')
    .map((formName) => formNameToDocKey[formName])
    .filter(Boolean) as DocumentKey[];

  return DOCUMENT_STEP_ORDER.filter((doc) => selectedDocKeys.includes(doc));
};

const getDocumentKeyForFormName = (formName: string): DocumentKey | null => {
  if (formName.includes('Consent Partnership')) return 'csConsentPartnership';
  if (formName === 'Consent' || formName.includes('Consent')) return 'consent';
  if (formName.includes('Release of Information')) return 'ecmRoi';
  if (formName.includes('Adult Comprehensive Assessment')) return 'adultEcmAssessment';
  if (formName.includes('Assessment (Children)')) return 'cyEcmAssessment';
  if (formName.includes('Housing Support Plan')) return 'ihsp';
  if (formName.includes('Housing Assessment')) return 'housingAssessment';
  if (formName.includes('Partnership Care Plan')) return 'partnershipCarePlan';
  if (formName.includes('Care Plan')) return 'carePlan';
  if (formName.includes('Community Support Services Referral Form')) return 'phpCsReferral';
  if (formName.includes('Enhanced Care Management (ECM) Referral Form for Children/Youth')) return 'ecmReferralChildYouth';
  if (formName.includes('Enhanced Care Management (ECM) Referral Form for Adults')) return 'ecmReferralAdult';
  if (formName.includes('Contra Costa Health Plan Child/Youth Enhanced Care Management (ECM) Referral Form')) return 'cchpEcmReferralChildYouth';
  if (formName.includes('Contra Costa Health Plan Adult Enhanced Care Management (ECM) Referral Form')) return 'cchpEcmReferralAdult';
  return null;
};

const mergeSelectionsFromGeneratedPDFs = (
  data: FormSubmission,
  generatedPDFs: Array<Pick<GeneratedPDF, 'formName'>> = []
): FormSubmission => {
  const generatedDocKeys = DOCUMENT_STEP_ORDER.filter((doc) =>
    generatedPDFs.some((pdf) => getDocumentKeyForFormName(pdf.formName) === doc)
  );

  if (generatedDocKeys.length === 0) {
    return data;
  }

  const selectedDocumentsSet = new Set<DocumentKey>(data.selectedDocuments || []);
  generatedDocKeys.forEach((doc) => selectedDocumentsSet.add(doc));

  const selectedGroup = data.documentSelection?.selectedGroup || '';
  const groupedDocuments = selectedGroup ? (GROUP_DOCUMENTS[selectedGroup] || []) : [];
  const individualDocumentsSet = new Set<DocumentKey>(data.documentSelection?.individualDocuments || []);

  generatedDocKeys
    .filter((doc) => !groupedDocuments.includes(doc))
    .forEach((doc) => individualDocumentsSet.add(doc));

  const mergedSelectedDocuments = DOCUMENT_STEP_ORDER.filter((doc) => selectedDocumentsSet.has(doc));
  const mergedIndividualDocuments = DOCUMENT_STEP_ORDER.filter(
    (doc) => individualDocumentsSet.has(doc) && !groupedDocuments.includes(doc)
  );

  return {
    ...data,
    selectedDocuments: mergedSelectedDocuments,
    documentSelection: {
      selectedGroup,
      individualDocuments: mergedIndividualDocuments,
      finalSelected: mergedSelectedDocuments,
    },
  };
};

// Production-ready default values - all empty

const getDefaultValues = (): FormSubmission => ({
  memberInfo: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    medicalId: '',
    phone: '',
    email: '',
    address: { street: '', city: '', state: 'CA', zipCode: '' },
  },
  roi: {
    informationToRelease: {
      carePlans: false,
      medicalHistory: false,
      medications: false,
      mentalHealth: false,
      substanceUse: false,
      hivAids: false,
      hospitalizationER: false,
      socialServices: false,
      billing: false,
      other: false,
      otherDescription: '',
    },
    providerFrom: {
      name: 'California Support Services 420 Railroad Ave. Suite #103, Pittsburg, CA 94565',
      address: '',
      phone: '',
      fax: '',
    },
    providerTo: {
      name: 'California Support Services 420 Railroad Ave. Suite #103, Pittsburg, CA 94565',
      address: '',
      phone: '',
      fax: '',
    },
    purposeOfDisclosure: {
      continuityOfCare: false,
      careCoordination: false,
      ecmServiceDelivery: false,
      carePlanning: false,
      eligibility: false,
      other: false,
      otherDescription: '',
    },
    expirationOption: '',
    specificExpirationDate: '',
    mentalHealthInitial: '',
    sudInitial: '',
    hivInitial: '',
    witnessName: '',
    witnessDate: '',
  },
  housing: {
    managedCarePlan: '',
    referralSource: {
      mcp: false,
      ecm: false,
      provider: false,
      self: false,
      other: false,
      otherDescription: '',
    },
    assessmentDate: new Date().toISOString().split('T')[0],
    assessorName: '',
    assessorTitle: '',
    assessorSignatureDate: '',
    currentSituation: '',
    currentSituationOther: '',
    lengthOfSituation: '',
    barriers: {
      lowIncome: false,
      poorCredit: false,
      priorEviction: false,
      criminalHistory: false,
      disability: false,
      behavioralHealth: false,
      lackDocuments: false,
      languageBarriers: false,
      transportationBarriers: false,
      financial: false,
      credit: false,
      rentalHistory: false,
      criminal: false,
      mentalHealth: false,
      substanceUse: false,
      family: false,
      documentation: false,
      other: false,
      otherDescription: '',
    },
    healthRelatedNeeds: '',
    relevantDiagnoses: '',
    howHousingHelps: '',
    diagnoses: [],
    primaryDiagnosis: '',
    navigation: {
      housingSearch: false,
      applications: false,
      documents: false,
      accommodation: false,
      landlordEngagement: false,
      leaseTerms: false,
      moveIn: false,
      rentalAssistance: false,
    },
    preferences: {
      locationCity: '',
      unitStudio: false,
      unit1BR: false,
      unit2BR: false,
      unitOther: false,
      unitOtherDescription: '',
      accessibilityNone: false,
      accessibilityADA: false,
      accessibilityOther: false,
      accessibilityOtherDescription: '',
    },
    income: {
      ssiSsdi: false,
      employment: false,
      ga: false,
      none: false,
      other: false,
      otherDescription: '',
    },
    sustaining: {
      newLease: false,
      existingLease: false,
      informal: false,
    },
    riskFactors: {
      rentUtilities: false,
      leaseViolations: false,
      conflict: false,
      maintainingUnit: false,
      missedAppointments: false,
      behavioralSymptoms: false,
      lackTenancySkills: false,
    },
    supportNeeds: {
      tenantRights: false,
      budgeting: false,
      utilitySetup: false,
      landlordComm: false,
      crisisPrevention: false,
      ongoingServices: false,
    },
    safety: {
      safetyConcernsYes: false,
      safetyConcernsNo: false,
      evictionRisk30Yes: false,
      evictionRisk30No: false,
      repeatedLossYes: false,
      repeatedLossNo: false,
      mitigationPlan: '',
    },
    memberGoals: '',
    housingGoals: '',
    shortTermGoals: '',
    longTermGoals: '',
    recommendation: {
      housingNavigation: false,
      housingSustaining: false,
      both: false,
      durationFrequency: '',
      rationale: '',
    },
    previousEvictions: false,
    evictionDetails: '',
    assessorSignature: '',
  },
  consent: {
    isMinor: false,
    signingOnBehalf: false,
    printedName: '',
    relationshipToMember: '',
  },
  carePlan: {
    ecmProvider: { name: 'California Support Services', npi: '', phone: '', address: '' },
    leadCareManager: { name: '', phone: '', email: '' },
    primaryCareProvider: { name: '', phone: '', address: '' },
    ecmTargetGraduationDate: '',
    postGraduationSupports: '',
    communitySupportsReferral: false,
    communitySupportsDetails: '',
    dsnpDualEligible: false,
    dsnpHealthPlan: '',
    memberComments: '',
    actualGraduationDate: '',
    planStartDate: new Date().toISOString().split('T')[0],
    planReviewDate: '',
    problems: [],
    memberParticipation: '',
    memberStrengths: '',
    memberPreferences: '',
    emergencyContact: { name: '', relationship: '', phone: '' },
  },
  partnershipConsent: {
    consentInitial: '',
    declineInitial: '',
    mentalHealthInitial: '',
    hivInitial: '',
    substanceUseInitial: '',
    expirationStandardInitial: '',
    expirationCustomInitial: '',
    customExpirationDate: '',
    repName: '',
    consentToShare: true,
    consentToCoordinate: true,
    consentToContact: true,
    organizationName: 'Partnership HealthPlan of California',
    mentalHealthConsent: false,
    substanceUseConsent: false,
    hivConsent: false,
  },
  cyEcmAssessment: getDefaultCYECMAssessmentData(),
  documentSelection: {
    selectedGroup: '',
    individualDocuments: [],
  },
  selectedDocuments: [],
  signature: {
    imageData: '',
    signedBy: '',
    signedDate: new Date().toISOString().split('T')[0],
    relationship: '',
    signatureType: 'draw',
    ipAddress: '',
  },
});

// Test data for development and testing
const getTestData = (): FormSubmission => ({
  memberInfo: {
    firstName: 'John',
    lastName: 'Doe',
    dateOfBirth: '1985-05-15',
    medicalId: 'MC' + Math.random().toString().substring(2, 12),
    phone: '(555) 123-4567',
    email: 'john.doe@example.com',
    address: {
      street: '123 Main Street',
      city: 'Los Angeles',
      state: 'CA',
      zipCode: '90001'
    },
  },
  roi: {
    ecmProviderName: 'California Support Services',
    informationToRelease: {
      carePlans: true,
      medicalHistory: true,
      medications: true,
      mentalHealth: true,
      substanceUse: true,
      hivAids: true,
      hospitalizationER: true,
      socialServices: true,
      billing: true,
      other: true,
      otherDescription: 'Lab results and diagnostic imaging',
    },
    providerFrom: {
      name: 'Downtown Medical Clinic',
      address: '456 Healthcare Blvd, Los Angeles, CA 90015',
      phone: '(555) 234-5678',
      fax: '(555) 234-5679'
    },
    providerTo: {
      name: 'Partnership HealthPlan of California',
      address: '4665 Business Center Dr, Fairfield, CA 94534',
      phone: '(707) 863-4100',
      fax: '(707) 863-4101'
    },
    purposeOfDisclosure: {
      continuityOfCare: false,
      careCoordination: false,
      ecmServiceDelivery: false,
      carePlanning: false,
      eligibility: false,
      other: false,
      otherDescription: '',
    },
    expirationOption: '',
    specificExpirationDate: '',
    mentalHealthInitial: 'JD',
    sudInitial: 'JD',
    hivInitial: 'JD',
    witnessName: 'Jane Smith',
    witnessDate: new Date().toISOString().split('T')[0],
    witnessSignature: 'Jane Smith',
  },
  housing: {
    managedCarePlan: 'Partnership HealthPlan of California',
    referralSource: {
      mcp: true,
      ecm: true,
      provider: true,
      self: true,
      other: true,
      otherDescription: 'Community Health Center referral',
    },
    assessmentDate: new Date().toISOString().split('T')[0],
    assessorName: 'Sarah Johnson',
    assessorTitle: 'Housing Navigator',
    assessorSignatureDate: new Date().toISOString().split('T')[0],
    currentSituation: 'homeless_unsheltered',
    currentSituationOther: '',
    lengthOfSituation: '6 months',
    barriers: {
      lowIncome: true,
      poorCredit: true,
      priorEviction: true,
      criminalHistory: true,
      disability: true,
      behavioralHealth: true,
      lackDocuments: true,
      languageBarriers: true,
      transportationBarriers: true,
      financial: true,
      credit: true,
      rentalHistory: true,
      criminal: true,
      mentalHealth: true,
      substanceUse: true,
      family: true,
      documentation: true,
      other: true,
      otherDescription: 'Limited social support network',
    },
    healthRelatedNeeds: 'Member requires stable housing to manage chronic health conditions including diabetes and hypertension. Difficulty accessing healthcare services due to unstable housing.',
    relevantDiagnoses: 'Type 2 Diabetes, Hypertension, Major Depressive Disorder',
    howHousingHelps: 'Stable housing will allow member to maintain medication schedule, attend regular medical appointments, and reduce stress contributing to health conditions.',
    diagnoses: [],
    primaryDiagnosis: 'Type 2 Diabetes Mellitus',
    navigation: {
      housingSearch: true,
      applications: true,
      documents: true,
      accommodation: true,
      landlordEngagement: true,
      leaseTerms: true,
      moveIn: true,
      rentalAssistance: true,
    },
    preferences: {
      locationCity: 'Los Angeles or surrounding area',
      unitStudio: true,
      unit1BR: true,
      unit2BR: true,
      unitOther: true,
      unitOtherDescription: 'Shared housing with roommate',
      accessibilityNone: false,
      accessibilityADA: true,
      accessibilityOther: true,
      accessibilityOtherDescription: 'Ground floor, close to public transit',
    },
    income: {
      ssiSsdi: true,
      employment: true,
      ga: true,
      none: false,
      other: true,
      otherDescription: 'CalFresh benefits',
    },
    sustaining: {
      newLease: true,
      existingLease: true,
      informal: true,
    },
    riskFactors: {
      rentUtilities: true,
      leaseViolations: true,
      conflict: true,
      maintainingUnit: true,
      missedAppointments: true,
      behavioralSymptoms: true,
      lackTenancySkills: true,
    },
    supportNeeds: {
      tenantRights: true,
      budgeting: true,
      utilitySetup: true,
      landlordComm: true,
      crisisPrevention: true,
      ongoingServices: true,
    },
    safety: {
      safetyConcernsYes: true,
      safetyConcernsNo: false,
      evictionRisk30Yes: true,
      evictionRisk30No: false,
      repeatedLossYes: true,
      repeatedLossNo: false,
      mitigationPlan: 'Regular case management check-ins, rental assistance coordination, crisis intervention services available 24/7.',
    },
    memberGoals: 'Find stable, affordable housing in safe neighborhood. Maintain housing long-term.',
    housingGoals: 'Secure studio or 1-bedroom apartment within 3 months. Maintain stable housing for minimum 12 months.',
    shortTermGoals: 'Complete housing applications, gather required documents, attend apartment viewings.',
    longTermGoals: 'Maintain stable housing, improve credit score, build positive rental history.',
    recommendation: {
      housingNavigation: true,
      housingSustaining: true,
      both: true,
      durationFrequency: '12 months, weekly check-ins for first 3 months, then bi-weekly',
      rationale: 'Member requires both navigation services to secure housing and sustaining services to maintain housing stability given identified risk factors.',
    },
    previousEvictions: false,
    evictionDetails: '',
    assessorSignature: 'Sarah Johnson',
  },
  consent: {
    isMinor: true,
    signingOnBehalf: true,
    printedName: 'John Doe',
    relationshipToMember: 'Self',
  },
  carePlan: {
    ecmProvider: {
      name: 'California Support Services',
      npi: '1234567890',
      phone: '(707) 863-4100',
      address: '4665 Business Center Dr, Fairfield, CA 94534',
    },
    leadCareManager: {
      name: 'Sarah Johnson',
      phone: '(555) 123-4567',
      email: 'sarah.johnson@partnershiphp.org',
    },
    primaryCareProvider: {
      name: 'Dr. Robert Smith',
      phone: '(555) 234-5678',
      address: '456 Medical Center, Los Angeles, CA 90002',
    },
    ecmTargetGraduationDate: '2025-12-31',
    postGraduationSupports: 'Transition to community-based support, ongoing PCP follow-up, peer support groups',
    communitySupportsReferral: true,
    communitySupportsDetails: 'Referral to community mental health services and housing support agency',
    dsnpDualEligible: true,
    dsnpHealthPlan: 'Partnership HealthPlan D-SNP',
    memberComments: 'Member is motivated to improve health outcomes and maintain stable housing',
    actualGraduationDate: '2025-11-30',
    planStartDate: new Date().toISOString().split('T')[0],
    planReviewDate: '2025-06-30',
    problems: [
      {
        id: '1',
        problemName: 'Chronic Disease Management',
        description: 'Difficulty managing multiple chronic conditions including Type 2 Diabetes and Hypertension',
        priority: 'High',
        startDate: '2024-01-15',
        lastReviewed: '2025-01-15',
        resolvedReason: '',
        resolvedReasonOther: '',
        comments: 'Requires ongoing education, medication management, and lifestyle modifications',
        goals: [
          {
            id: '1',
            goalName: 'Improve diabetes control',
            description: 'Member will achieve HbA1c below 7.0% within 6 months through medication adherence and diet changes',
            startDate: '2024-01-15',
            priority: 'High',
            percentComplete: '60',
            duration: 'Short Term',
            closedDate: '',
            readinessToChange: 'Action',
            closedReason: '',
            comments: 'HbA1c decreased from 9.2% to 7.8%. Continuing diet education.',
            outcome: 'Improved glucose control and reduced complications',
            targetDate: '2025-07-15',
            interventions: [
              {
                id: '1',
                interventionName: 'Diabetes Self-Management Education',
                description: 'Provide comprehensive diabetes education including blood glucose monitoring, medication adherence, and diet planning',
                startDate: '2024-01-15',
                priority: 'High',
                targetDate: '2025-04-15',
                type: 'Coordination',
                category: 'Medical',
                closedDate: '',
                effective: 'Yes',
                closedReason: '',
                comments: 'Completed 4 of 6 education sessions. Member demonstrates improved understanding.',
              },
              {
                id: '2',
                interventionName: 'PCP Follow-up Coordination',
                description: 'Schedule and coordinate regular PCP visits for medication adjustment and lab work',
                startDate: '2024-02-01',
                priority: 'High',
                targetDate: '2025-06-01',
                type: 'Coordination',
                category: 'Medical',
                closedDate: '',
                effective: 'Yes',
                closedReason: '',
                comments: 'Regular quarterly visits scheduled and attended.',
              },
              {
                id: '3',
                interventionName: 'Nutritional Counseling Referral',
                description: 'Refer member to registered dietitian for personalized meal planning',
                startDate: '2024-03-01',
                priority: 'Medium',
                targetDate: '2025-03-01',
                type: 'Coordination',
                category: 'Medical',
                closedDate: '',
                effective: 'Yes',
                closedReason: '',
                comments: 'Member enrolled in nutritional counseling program.',
              },
              {
                id: '4',
                interventionName: 'Medication Reconciliation',
                description: 'Review all medications with pharmacy to identify interactions and optimize regimen',
                startDate: '2024-01-20',
                priority: 'High',
                targetDate: '2025-02-20',
                type: 'Coordination',
                category: 'Medical',
                closedDate: '2025-02-15',
                effective: 'Yes',
                closedReason: 'Completed',
                comments: 'Medication list reconciled. Two duplicate meds removed.',
              },
              {
                id: '5',
                interventionName: 'Community Health Worker Support',
                description: 'Assign CHW for regular home visits to reinforce self-management skills',
                startDate: '2024-04-01',
                priority: 'Medium',
                targetDate: '2025-04-01',
                type: 'Coordination',
                category: 'Other',
                closedDate: '',
                effective: 'Yes',
                closedReason: '',
                comments: 'CHW conducting bi-weekly home visits. Member engaged.',
              },
            ],
          },
          {
            id: '2',
            goalName: 'Blood pressure management',
            description: 'Member will maintain BP below 140/90 through medication adherence and lifestyle modifications',
            startDate: '2024-01-15',
            priority: 'High',
            percentComplete: '70',
            duration: 'Long Term',
            closedDate: '',
            readinessToChange: 'Preparation',
            closedReason: '',
            comments: 'BP trending downward. Currently averaging 135/85.',
            outcome: 'Stable blood pressure within normal range',
            targetDate: '2025-12-31',
            interventions: [
              {
                id: '1',
                interventionName: 'Blood Pressure Monitoring Education',
                description: 'Teach member to use home BP monitor and maintain log',
                startDate: '2024-02-01',
                priority: 'High',
                targetDate: '2025-03-01',
                type: 'Coordination',
                category: 'Medical',
                closedDate: '',
                effective: 'Yes',
                closedReason: '',
                comments: 'Member purchased home BP monitor. Logging daily readings.',
              },
              {
                id: '2',
                interventionName: 'Exercise Program Referral',
                description: 'Refer to community exercise program suitable for chronic conditions',
                startDate: '2024-03-15',
                priority: 'Medium',
                targetDate: '2025-06-15',
                type: 'Coordination',
                category: 'Other',
                closedDate: '',
                effective: 'Yes',
                closedReason: '',
                comments: 'Enrolled in Silver Sneakers program. Attending 2x/week.',
              },
            ],
          },
        ],
      },
    ],
    memberParticipation: 'Active participation in care planning. Member attends all scheduled meetings and provides input on goals.',
    memberStrengths: 'Motivated, good family support, reliable transportation, English-speaking, consistent with appointments',
    memberPreferences: 'Prefers in-person appointments, morning schedule, female providers when available',
    emergencyContact: {
      name: 'Mary Doe',
      relationship: 'Mother',
      phone: '(555) 987-6543',
    },
  },
  partnershipConsent: {
    consentInitial: 'JD',
    declineInitial: 'JD',
    mentalHealthInitial: 'JD',
    hivInitial: 'JD',
    substanceUseInitial: 'JD',
    expirationStandardInitial: 'JD',
    expirationCustomInitial: 'JD',
    customExpirationDate: '2025-12-31',
    repName: 'John Doe',
    consentToShare: true,
    consentToCoordinate: true,
    consentToContact: true,
    organizationName: 'Partnership HealthPlan of California',
    mentalHealthConsent: true,
    substanceUseConsent: true,
    hivConsent: true,
  },
  cyEcmAssessment: getTestCYECMAssessmentData(),
  adultEcmAssessment_flat: getTestAdultEcmFlatMap(),
  documentSelection: {
    selectedGroup: 'contra_costa_adult',
    individualDocuments: ['csConsentPartnership', 'cyEcmAssessment', 'housingAssessment', 'carePlan'],
  },
  selectedDocuments: ['consent', 'csConsentPartnership', 'ecmRoi', 'adultEcmAssessment', 'cyEcmAssessment', 'housingAssessment', 'carePlan'],
  signature: {
    // Small white PNG 1x1 used for dev/testing so generation can proceed
    imageData: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=',
    signedBy: 'John Doe',
    signedDate: new Date().toISOString().split('T')[0],
    relationship: 'Self',
    signatureType: 'draw',
    ipAddress: '127.0.0.1',
  },
});

// View states  
type ViewState = 'form' | 'preview' | 'success';

interface ValidationError {
  field: string;
  message: string;
  step: number;
}

const FormWizard: React.FC = () => {
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const { setViewState: setGlobalViewState } = useViewState();

  // Validation functions for each step.
  // skipLockedFieldChecks: at PDF-generation time we don't re-require DOB/
  // address — those are locked-blank for an existing-member match, and
  // whether that lock is still in sync with client state shouldn't block
  // generation. Step-by-step navigation still enforces them normally.
  const validateStep = (
    stepId: string,
    data: FormSubmission,
    selectedDocuments: DocumentKey[],
    skipLockedFieldChecks: boolean = false
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    switch (stepId) {
      case 'document-selection':
        // Skip validation at generate time — selection is already enforced at step navigation
        break;

      case 'member-info':
        if (!data.memberInfo.firstName?.trim()) errors.push(t('memberInfo.firstNameRequired'));
        if (!data.memberInfo.lastName?.trim()) errors.push(t('memberInfo.lastNameRequired'));
        if (data.memberInfo.phone?.trim() && !/^\d{10}$/.test(data.memberInfo.phone)) {
          errors.push(t('validation.phoneNumberFormat'));
        }
        if (data.memberInfo.email?.trim() && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(data.memberInfo.email)) {
          errors.push(t('validation.emailFormat'));
        }
        if (data.memberInfo.address.zipCode?.trim() && !/^\d{5}(-\d{4})?$/.test(data.memberInfo.address.zipCode)) {
          errors.push(t('validation.zipCodeFormat'));
        }
        if (!foundExistingMember && !skipLockedFieldChecks) {
          // These fields are locked and intentionally left blank when we
          // matched an existing member — their real values never leave the
          // backend, so they can't be required in that case.
          if (!data.memberInfo.dateOfBirth) errors.push(t('memberInfo.dateOfBirthRequired'));
          if (!data.memberInfo.address.street?.trim()) errors.push(t('memberInfo.streetRequired'));
          if (!data.memberInfo.address.city?.trim()) errors.push(t('memberInfo.cityRequired'));
          if (!data.memberInfo.address.state?.trim()) errors.push(t('memberInfo.stateRequired'));
          if (!data.memberInfo.address.zipCode?.trim()) errors.push(t('validation.zipCodeRequired'));
        }
        if (!data.memberInfo.medicalId?.trim()) errors.push(t('memberInfo.medicalIdRequired'));
        break;

      case 'roi':
        break;

      case 'housing':
        break;

      case 'consent':
        // Consent form displays full notice text, no validation needed except for authorized representative fields
        if (data.consent.signingOnBehalf) {
          if (!data.consent.printedName?.trim()) errors.push(t('validation.printedNameRequiredOnBehalf'));
          if (!data.consent.relationshipToMember?.trim()) errors.push(t('validation.relationshipRequired'));
        }
        break;

      case 'care-plan':
        // Provider name is fixed to "California Support Services" — no validation needed
        if (!data.carePlan.leadCareManager.name?.trim()) errors.push(t('validation.leadCareManagerRequired'));
        if (!data.carePlan.problems || data.carePlan.problems.length === 0) {
          errors.push(t('validation.carePlanProblemRequired'));
        }
        break;

      case 'partnership':
        break;
      case 'cy-ecm-assessment':
        // Placeholder: no strict required fields here by default. Add checks if specific ECM fields must be filled before signing.
        break;

      case 'adult-ecm-assessment':
        // No strict required validation for this step
        break;

      case 'signature':
        {
          // Signature is optional for initial submit. If partially provided, enforce completion.
          const hasImage = !!data.signature.imageData?.trim();
          const hasName = !!data.signature.signedBy?.trim();
          const hasDate = !!data.signature.signedDate;
          // signedDate may auto-fill by UI, so do not treat date-only as an attempted signature
          const hasAny = hasImage || hasName;
          if (hasAny) {
            if (!hasName) errors.push(t('validation.printedNameRequiredSigning'));
            if (!hasImage) errors.push(t('validation.digitalSignatureRequired'));
            if (!hasDate) errors.push(t('validation.signatureDateRequired'));
          }
        }
        break;
    }

    return { isValid: errors.length === 0, errors };
  };

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [viewState, setViewState] = useState<ViewState>('form');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState<string>('');
  const [generatedPDFs, setGeneratedPDFs] = useState<GeneratedPDF[]>([]);
  const [submissionId, setSubmissionId] = useState<string>('');
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [localDraftKey, setLocalDraftKey] = useState<string | null>(null);
  const [localDrafts, setLocalDrafts] = useState<import('../services/api').LocalDraft[]>([]);
  const [showLocalDraftsPanel, setShowLocalDraftsPanel] = useState(false);
  const [stepErrors, setStepErrors] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [medicalIdExists, setMedicalIdExists] = useState(false);
  const [medicalIdConflictingDocuments, setMedicalIdConflictingDocuments] = useState<DocumentKey[]>([]);
  // True when MemberInfoSection matched an existing member by CIN (no
  // conflicting document types) and locked their non-name fields. Those
  // fields are intentionally left blank (their real values never leave the
  // backend), so required-field validation must skip them in that case.
  const [foundExistingMember, setFoundExistingMember] = useState(false);
  // When an existing member is matched on a brand-new submission and they
  // have a prior signature on file, this holds their medicalId so the
  // Signature step can offer "use existing" vs "sign new". The real
  // signature data is never fetched to the client for this flow — only this
  // flag + medicalId are known here; the backend looks up and applies the
  // real signature itself at PDF-generation time (see reuseSignatureFrom).
  const [existingMemberHasSignature, setExistingMemberHasSignature] = useState(false);
  const [existingMemberMedicalId, setExistingMemberMedicalId] = useState<string | null>(null);
  // The user's choice in the new-submission "use existing signature" prompt.
  // null = not decided yet / not applicable.
  const [reuseSignatureForNewMember, setReuseSignatureForNewMember] = useState<boolean | null>(null);
  const handleReuseSignatureChoice = useCallback((reuse: boolean) => {
    setReuseSignatureForNewMember(reuse);
  }, []);
  // Stable reference: MemberInfoSection's effect depends on this callback, so
  // a new function identity on every render would re-trigger that effect
  // (including its state updates) in an infinite loop.
  const handleMedicalIdStatusChange = useCallback((exists: boolean, conflictingDocuments?: DocumentKey[]) => {
    setMedicalIdExists(exists);
    setMedicalIdConflictingDocuments(conflictingDocuments || []);
  }, []);
  const handleFoundExistingMemberChange = useCallback((found: boolean, hasReusableSignature?: boolean, medicalId?: string) => {
    setFoundExistingMember(found);
    setExistingMemberHasSignature(found && !!hasReusableSignature);
    setExistingMemberMedicalId(found && medicalId ? medicalId : null);
  }, []);
  const [isEditMode, setIsEditMode] = useState(false);
  // Snapshot of the signature exactly as it was when an existing submission
  // was loaded for editing — used so the Signature step can offer "reuse
  // the existing signature" for newly-added documents without depending on
  // live (possibly since-modified) form state.
  const [originalSignature, setOriginalSignature] = useState<FormSubmission['signature'] | null>(null);
  // Which document types this submission actually had at load time — used
  // (instead of the URL's selectedPDFs param, which isn't always present)
  // to detect "newly added" documents for ANY edit session, regardless of
  // how the edit link was opened.
  const [originalDocuments, setOriginalDocuments] = useState<DocumentKey[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [accessMode, setAccessMode] = useState<'signature' | 'full' | null>(null);
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [totalPDFsInSubmission, setTotalPDFsInSubmission] = useState<number | null>(null);
  const [hasUserModifiedDocuments, setHasUserModifiedDocuments] = useState(false);
  const adultFlatRef = useRef<Record<string, string | number | boolean | undefined> | null>(null);
  const cyFlatRef = useRef<Record<string, string | number | boolean | undefined>>({});
  const initializedCarePlanModeRef = useRef<string | null>(null);

  // "On behalf of" feature — admin only
  const isAdmin = isAdminDashboardUser();
  const currentUser = getDashboardUser();
  const [staffUsers, setStaffUsers] = useState<Array<{ id: number | string; firstName: string; lastName: string; email: string; role: string }>>([]);
  const [onBehalfOf, setOnBehalfOf] = useState<{ userId: string | number; userName: string } | null>(null);
  const [staffUsersLoading, setStaffUsersLoading] = useState(false);

  // Fetch approved staff users for the dropdown (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const fetchStaff = async () => {
      setStaffUsersLoading(true);
      try {
        const result = await formsAPI.getApprovedStaffUsers();
        if (!cancelled && result.success && result.users) {
          setStaffUsers(result.users);
        }
      } catch (e) {
        console.error('Failed to fetch staff users:', e);
      } finally {
        if (!cancelled) setStaffUsersLoading(false);
      }
    };
    fetchStaff();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // Default onBehalfOf to the admin themselves
  useEffect(() => {
    if (isAdmin && currentUser && !onBehalfOf) {
      setOnBehalfOf({
        userId: currentUser.id,
        userName: `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email,
      });
    }
  }, [isAdmin, currentUser, onBehalfOf]);

  const methods = useForm<FormSubmission>({
    defaultValues: getDefaultValues(),
    mode: 'onBlur',
  });

  const { watch, reset, getValues, setValue, formState } = methods;
  const formData = watch();

  const selectedGroup = watch('documentSelection.selectedGroup');
  const individualDocuments = watch('documentSelection.individualDocuments');
  const finalSelectedRaw = watch('documentSelection.finalSelected');
  // Stabilize reference: watch() returns a new [] fallback every render when
  // the field is undefined, which otherwise defeats useMemo/useEffect deps
  // downstream and causes render loops.
  const finalSelected = useMemo<DocumentKey[]>(
    () => finalSelectedRaw || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finalSelectedRaw ? finalSelectedRaw.join(',') : '']
  );
  const editParams = new URLSearchParams(location.search);
  const editSubmissionId = editParams.get('editSubmissionId');
  const selectedPDFsParam = editParams.get('selectedPDFs');
  const initialEditSelectedDocuments = useMemo(
    () => getSelectedDocKeysFromParam(selectedPDFsParam),
    [selectedPDFsParam]
  );
  const canModifyEditSelection = !!editSubmissionId && !!selectedPDFsParam;

  const selectedDocuments = useMemo<DocumentKey[]>(() => {
    // Check if we're in selective edit mode
    const params = new URLSearchParams(location.search);
    const carePlanMode = params.get('carePlanMode');
    const existingSubmissionId = params.get('existingSubmissionId');

    if (carePlanMode === 'true' && existingSubmissionId) {
      return ['carePlan'];
    }

    if (editSubmissionId && selectedPDFsParam) {
      if (hasUserModifiedDocuments) {
        return DOCUMENT_STEP_ORDER.filter((doc) => finalSelected.includes(doc));
      }

      return initialEditSelectedDocuments;
    }

    // Normal flow - use form selections
    const groupDocs = selectedGroup ? (GROUP_DOCUMENTS[selectedGroup as DocumentGroupKey] || []) : [];
    const groupSet = new Set(groupDocs);
    const individualDocs = Array.isArray(individualDocuments)
      ? individualDocuments.filter((doc) => !groupSet.has(doc))
      : [];

    const deduped = Array.from(new Set<DocumentKey>([...groupDocs, ...individualDocs]));
    return DOCUMENT_STEP_ORDER.filter((doc) => deduped.includes(doc));
  }, [
    selectedGroup,
    individualDocuments,
    location.search,
    hasUserModifiedDocuments,
    finalSelected,
    editSubmissionId,
    selectedPDFsParam,
    initialEditSelectedDocuments,
    canModifyEditSelection,
  ]);

  // Documents currently selected that weren't part of the submission when
  // it was originally loaded for editing — these are the ones a "sign
  // fresh" choice would apply to, versus documents that already have a
  // real signature baked into their existing generated PDF. Compared
  // against originalDocuments (a snapshot taken at load time), not the
  // URL's selectedPDFs param, so this works for ANY edit session
  // regardless of how the edit link was opened.
  const newlyAddedDocuments = useMemo(
    () => selectedDocuments.filter((doc) => !originalDocuments.includes(doc)),
    [selectedDocuments, originalDocuments]
  );
  const hasNewlyAddedDocuments = isEditMode && newlyAddedDocuments.length > 0 && !!originalSignature;

  useEffect(() => {
    setValue('selectedDocuments', selectedDocuments, { shouldDirty: false });
  }, [selectedDocuments, setValue]);

  const editSelectionSeededForRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (editSubmissionId && selectedPDFsParam) {
      // Seed finalSelected from the original submission exactly once per edit
      // session, so later user edits (checking new boxes) are never stomped
      // by this effect re-running.
      if (editSelectionSeededForRef.current !== editSubmissionId) {
        editSelectionSeededForRef.current = editSubmissionId;
        setValue('documentSelection.finalSelected', initialEditSelectedDocuments, { shouldDirty: false });
      }
      return;
    }

    editSelectionSeededForRef.current = null;
    const currentFinalSelected = getValues('documentSelection.finalSelected') || [];
    const isSame = currentFinalSelected.length === selectedDocuments.length
      && currentFinalSelected.every((doc, index) => doc === selectedDocuments[index]);
    if (!isSame) {
      setValue('documentSelection.finalSelected', selectedDocuments, { shouldDirty: false });
    }
  }, [
    editSubmissionId,
    selectedPDFsParam,
    initialEditSelectedDocuments,
    selectedDocuments,
    setValue,
    getValues,
  ]);

  // Track when user modifies document selection in the Document Selection step
  useEffect(() => {
    if (!canModifyEditSelection) {
      setHasUserModifiedDocuments(false);
      return;
    }

    const normalizedFinalSelected = DOCUMENT_STEP_ORDER.filter((doc) => finalSelected.includes(doc));
    const hasChanged = normalizedFinalSelected.length !== initialEditSelectedDocuments.length
      || normalizedFinalSelected.some((doc, index) => doc !== initialEditSelectedDocuments[index]);

    setHasUserModifiedDocuments(hasChanged);
  }, [canModifyEditSelection, finalSelected, initialEditSelectedDocuments]);

  // Check if user needs to select initial language
  useEffect(() => {
    const checkLanguageSelection = () => {
      // Only show language picker on first visit to avoid interrupting edit sessions
      const params = new URLSearchParams(location.search);
      const hasAccessToken = params.get('accessToken');
      const hasEditId = params.get('editSubmissionId');
      const carePlanMode = params.get('carePlanMode');
      
      if (hasAccessToken || hasEditId || carePlanMode) {
        // User is editing, has access token, or in care plan mode - don't show language picker
        return;
      }

      try {
        // Check if user has made an explicit language choice before
        const hasChosenLanguage = window.localStorage.getItem('css_forms_language_chosen');
        if (!hasChosenLanguage) {
          // No explicit choice made yet, show picker
          setShowLanguagePicker(true);
        }
      } catch {
        // localStorage not available, don't show picker
      }
    };

    checkLanguageSelection();
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('accessToken');
    if (!token) return;

    const loadAccessSession = async () => {
      try {
        const response = await formsAPI.getAccessSession(token);
        if (response.success && response.formData) {
          const data = response.formData as FormSubmission;
          
          // Auto-detect and set language from existing form data
          if (data.language && (data.language === 'es' || data.language === 'en')) {
            setLanguage(data.language);
          }
          
          reset(data);
          // Force setValue for documentSelection to trigger watch/useMemo recalculation
          if (data.documentSelection) {
            setValue('documentSelection.selectedGroup', data.documentSelection.selectedGroup || '', { shouldDirty: false });
            setValue('documentSelection.individualDocuments', data.documentSelection.individualDocuments || [], { shouldDirty: false });
          }
          if (data.selectedDocuments) {
            setValue('selectedDocuments', data.selectedDocuments, { shouldDirty: false });
          }
          setAccessToken(token);
          setAccessMode(response.mode);
          if (response.submissionId) {
            setSubmissionId(response.submissionId);
          }
          // Patient access: jump directly to signature step
          setTimeout(() => {
            const sigIdx = steps.findIndex(s => s.id === 'signature');
            if (sigIdx >= 0) setCurrentStepIndex(sigIdx);
          }, 200);
        }
      } catch (error) {
        console.error('Failed to load access session:', error);
      }
    };

    loadAccessSession();
  }, [location.search, reset]);

  // Admin direct edit: load submission data by ID (admin-only, no token/email needed)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('editSubmissionId');
    if (!editId) return;

    // Only allow edit mode if admin is logged in
    const dashboardToken = getDashboardToken();
    if (!dashboardToken) {
      console.error('Edit mode requires admin authentication');
      return;
    }

    // Admin edit is mutually exclusive with patient-access mode. If this tab
    // previously loaded a patient access link (accessToken state set) and
    // then navigated to an admin edit URL via client-side routing, that
    // stale token would otherwise still be sent as editContext on /generate,
    // which the backend correctly rejects with 401.
    setAccessToken(null);
    setAccessMode(null);

    const loadSubmissionForEdit = async () => {
      try {
        const response = await formsAPI.getSubmission(editId);
        if (response.formData) {
          const data = mergeSelectionsFromGeneratedPDFs(
            response.formData as FormSubmission,
            response.generatedPDFs || []
          );
          
          // Store the total PDF count from this submission
          setTotalPDFsInSubmission(response.generatedPDFs?.length || 0);
          
          // Auto-detect and set language from existing form data
          if (data.language && (data.language === 'es' || data.language === 'en')) {
            setLanguage(data.language);
          }
          
          reset(data);
          if (data.documentSelection) {
            setValue('documentSelection.selectedGroup', data.documentSelection.selectedGroup || '', { shouldDirty: false });
            setValue('documentSelection.individualDocuments', data.documentSelection.individualDocuments || [], { shouldDirty: false });
            setValue('documentSelection.finalSelected', data.documentSelection.finalSelected || data.selectedDocuments || [], { shouldDirty: false });
          }
          if (data.selectedDocuments) {
            setValue('selectedDocuments', data.selectedDocuments, { shouldDirty: false });
          }
          // Snapshot the signature as loaded, before any edits, so "reuse
          // existing signature" always has the real original to fall back
          // on regardless of what happens on the Signature step afterward.
          setOriginalSignature(data.signature?.imageData ? data.signature : null);
          // Snapshot which documents this submission actually had, so
          // "newly added" can be detected for any edit session — not just
          // ones opened via a link that happens to carry a selectedPDFs
          // query param.
          setOriginalDocuments(data.selectedDocuments || []);
          setSubmissionId(response.submissionId || editId);
          setIsEditMode(true);
          setShowRestoreDialog(false);
        }
      } catch (error) {
        console.error('Failed to load submission for editing:', error);
      }
    };

    loadSubmissionForEdit();
  }, [location.search, reset, setValue]);

  // Resume draft: load draft data by ID (admin-only)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const resumeDraftId = params.get('draftId');
    if (!resumeDraftId) return;

    const dashboardToken = getDashboardToken();
    if (!dashboardToken) return;

    const loadDraft = async () => {
      try {
        const response = await formsAPI.getSubmission(resumeDraftId);
        if (response.formData) {
          const data = response.formData as FormSubmission;
          
          // Auto-detect and set language from existing form data
          if (data.language && (data.language === 'es' || data.language === 'en')) {
            setLanguage(data.language);
          }
          
          reset(data);
          if (data.documentSelection) {
            setValue('documentSelection.selectedGroup', data.documentSelection.selectedGroup || '', { shouldDirty: false });
            setValue('documentSelection.individualDocuments', data.documentSelection.individualDocuments || [], { shouldDirty: false });
          }
          if (data.selectedDocuments) {
            setValue('selectedDocuments', data.selectedDocuments, { shouldDirty: false });
          }
          setDraftId(resumeDraftId);
          setSubmissionId(resumeDraftId);
          setIsEditMode(true);
          setShowRestoreDialog(false);
          // Restore to saved step after a short delay (allow steps to recalculate)
          const savedStep = response.draftStep;
          if (typeof savedStep === 'number' && savedStep > 0) {
            setTimeout(() => setCurrentStepIndex(savedStep), 100);
          }
        }
      } catch (error) {
        console.error('Failed to load draft:', error);
        // Save whatever is in the form to localStorage so data isn't lost
        const currentData = getValues();
        const hasData = !!(currentData.memberInfo?.firstName || currentData.memberInfo?.lastName || currentData.memberInfo?.medicalId);
        if (hasData) {
          const key = localStorageAPI.saveFormProgress(currentData, undefined);
          setLocalDraftKey(key);
        }
      }
    };

    loadDraft();
  }, [location.search, reset, setValue, getValues]);

  // Reset form to default state for new forms
  const resetFormToDefault = useCallback(() => {
    // Reset form data
    reset(getDefaultValues());
    
    // Reset all state variables
    setCurrentStepIndex(0);
    setViewState('form');
    setIsGenerating(false);
    setGenerateProgress('');
    setGeneratedPDFs([]);
    setSubmissionId('');
    setShowRestoreDialog(false);
    setStepErrors([]);
    setValidationErrors([]);
    setMedicalIdExists(false);
    setIsEditMode(false);
    setIsShareModalOpen(false);
    setDraftId(null);
    setIsSavingDraft(false);
    setAccessToken(null);
    setAccessMode(null);
    setShowLanguagePicker(false);
    
    // Clear assessment refs
    adultFlatRef.current = null;
    cyFlatRef.current = {};
    
    // Clear any local storage drafts (optional - keeps language preference)
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('form_draft_') || key.includes('submission')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [reset]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const carePlanMode = params.get('carePlanMode');
    const existingSubmissionId = params.get('existingSubmissionId');

    if (carePlanMode !== 'true' || !existingSubmissionId) {
      initializedCarePlanModeRef.current = null;
      return;
    }

    if (initializedCarePlanModeRef.current === existingSubmissionId) {
      return;
    }

    initializedCarePlanModeRef.current = existingSubmissionId;

    const loadExistingSubmissionForCarePlan = async () => {
      try {
        const response = await formsAPI.getSubmission(existingSubmissionId);
        const existingFormData = response.formData as FormSubmission | null;
        const existingMemberInfo = existingFormData?.memberInfo || response.memberInfo;

        if (!existingMemberInfo) {
          throw new Error('Existing submission is missing member info');
        }

        const nextValues: FormSubmission = {
          ...getDefaultValues(),
          ...(existingFormData?.language ? { language: existingFormData.language } : {}),
          memberInfo: {
            firstName: existingMemberInfo.firstName || '',
            lastName: existingMemberInfo.lastName || '',
            dateOfBirth: toDateInputValue(existingMemberInfo.dateOfBirth),
            medicalId: existingMemberInfo.medicalId || '',
            phone: existingMemberInfo.phone || '',
            email: existingMemberInfo.email || '',
            address: {
              street: existingMemberInfo.address?.street || '',
              city: existingMemberInfo.address?.city || '',
              state: existingMemberInfo.address?.state || 'CA',
              zipCode: existingMemberInfo.address?.zipCode || '',
            },
          },
          documentSelection: {
            selectedGroup: '',
            individualDocuments: ['carePlan'],
            finalSelected: ['carePlan'],
          },
          selectedDocuments: ['carePlan'],
        };

        if (existingFormData?.language && (existingFormData.language === 'en' || existingFormData.language === 'es')) {
          setLanguage(existingFormData.language);
        }

        reset(nextValues);
        setValue('documentSelection.selectedGroup', '', { shouldDirty: false });
        setValue('documentSelection.individualDocuments', ['carePlan'], { shouldDirty: false });
        setValue('documentSelection.finalSelected', ['carePlan'], { shouldDirty: false });
        setValue('selectedDocuments', ['carePlan'], { shouldDirty: false });
        setSubmissionId(existingSubmissionId);
        setShowRestoreDialog(false);
        setCurrentStepIndex(1);
      } catch (error) {
        initializedCarePlanModeRef.current = null;
        console.error('Failed to load existing submission for Care Plan mode:', error);
      }
    };

    loadExistingSubmissionForCarePlan();
  }, [location.search, reset, setLanguage, setValue]);

  // Sync local viewState with global context for navigation visibility
  useEffect(() => {
    setGlobalViewState(viewState);
    
    // Cleanup: reset to form when component unmounts
    return () => {
      setGlobalViewState('form');
    };
  }, [viewState, setGlobalViewState]);

  // Detect if this is a new form (no edit/access/draft parameters) and reset
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasAccessToken = params.get('accessToken');
    const hasEditId = params.get('editSubmissionId');
    const hasDraftId = params.get('draftId');
    const hasCarePlanMode = params.get('carePlanMode') === 'true' && !!params.get('existingSubmissionId');
    
    // If no URL parameters indicating edit/resume, this is a new form
    const isNewForm = !hasAccessToken && !hasEditId && !hasDraftId && !hasCarePlanMode;
    
    if (isNewForm) {
      resetFormToDefault();
    }
  }, [location.search, resetFormToDefault]);

  const steps = useMemo<FormWizardStep[]>(() => {
    const DOCUMENT_TO_STEP = getLocalizedDocumentToStep(language);
    const dynamicDocSteps = selectedDocuments.map((doc) => DOCUMENT_TO_STEP[doc]);
    
    // Check if we're in care plan mode
    const params = new URLSearchParams(location.search);
    const carePlanMode = params.get('carePlanMode');
    const existingSubmissionId = params.get('existingSubmissionId');
    
    // Handle Care Plan standalone mode
    if (carePlanMode === 'true' && existingSubmissionId) {
      // Skip directly to Care Plan - only member info and care plan steps needed
      return [
        { 
          id: 'member-info' as FormStep, 
          title: language === 'es' ? 'Información del Miembro' : 'Member Info', 
          description: language === 'es' ? 'Verificar información básica' : 'Verify basic information', 
          isComplete: false 
        },
        { 
          id: 'care-plan' as FormStep, 
          title: language === 'es' ? 'Plan de Atención' : 'Care Plan', 
          description: language === 'es' ? 'Plan de atención del miembro' : 'Member care plan', 
          isComplete: false 
        },
        { 
          id: 'signature' as FormStep, 
          title: language === 'es' ? 'Firma' : 'Signature', 
          description: language === 'es' ? 'Firma digital' : 'Digital signature', 
          isComplete: false 
        }
      ];
    }
    
    // Always show Document Selection when editing an existing submission, so the
    // user can add documents beyond the ones originally submitted.
    const includeDocumentSelection = true;

    const baseSteps: FormWizardStep[] = [
      { 
        id: 'member-info' as FormStep, 
        title: language === 'es' ? 'Información del Miembro' : 'Member Info', 
        description: language === 'es' ? 'Detalles básicos del miembro' : 'Basic member details', 
        isComplete: false 
      },
      ...dynamicDocSteps,
      { 
        id: 'signature' as FormStep, 
        title: language === 'es' ? 'Firma' : 'Signature', 
        description: language === 'es' ? 'Firma digital' : 'Digital signature', 
        isComplete: false 
      },
    ];

    // Add document selection at the beginning if needed
    if (includeDocumentSelection) {
      baseSteps.unshift({ 
        id: 'document-selection' as FormStep, 
        title: language === 'es' ? 'Selección de Documentos' : 'Document Selection', 
        description: language === 'es' ? 'Elija grupo y documentos individuales' : 'Choose group and individual documents', 
        isComplete: false 
      });
    }
    
    return baseSteps;
  }, [selectedDocuments, language, location.search]);

  useEffect(() => {
    if (accessMode === 'signature') {
      const idx = steps.findIndex((s) => s.id === 'signature');
      if (idx >= 0) setCurrentStepIndex(idx);
    }
  }, [accessMode, steps]);

  // Force-sync Adult ECM iframe values into form state before navigation/generation
  const collectAdultAssessmentFromIframe = useCallback((): Record<string, string | number | boolean | undefined> | null => {
    try {
      const iframe = (document.querySelector('iframe[title="Adult ECM Assessment Form"]') || document.querySelector('iframe[title="Formulario de Evaluación Integral Adulta de ECM"]')) as HTMLIFrameElement | null;
      const doc = iframe?.contentWindow?.document;
      if (!doc) return null;

      const map: Record<string, string | number | boolean | undefined> = {};
      const fields = doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input[id], textarea[id], select[id]'
      );
      fields.forEach((el) => {
        if (!el.id) return;
        if (isCheckboxInput(el)) {
          map[el.id] = !!el.checked;
        } else {
          map[el.id] = el.value ?? '';
        }
      });

      return map;
    } catch (err) {
      console.error('Failed to sync Adult ECM iframe:', err);
      return null;
    }
  }, []);

  const syncAdultAssessmentFromIframe = useCallback(() => {
    const map = collectAdultAssessmentFromIframe();
    if (map) {
      adultFlatRef.current = map;
      setValue('adultEcmAssessment_flat', map, { shouldDirty: true });
    }
    return map;
  }, [collectAdultAssessmentFromIframe, setValue]);

  // Force-sync CY ECM iframe values into form state before navigation/generation
  const collectCyAssessmentFromIframe = useCallback((): Record<string, string | number | boolean | undefined> | null => {
    try {
      const iframe = (document.querySelector('iframe[title="CY ECM Assessment Form"]') || document.querySelector('iframe[title="Formulario de Evaluación ECM N/J"]')) as HTMLIFrameElement | null;
      const doc = iframe?.contentWindow?.document;
      if (!doc) return null;

      const map: Record<string, string | number | boolean | undefined> = {};
      const fields = doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input[id], textarea[id], select[id]'
      );
      fields.forEach((el) => {
        if (!el.id) return;
        if (isCheckboxInput(el)) {
          map[el.id] = !!el.checked;
        } else {
          map[el.id] = el.value ?? '';
        }
      });

      return map;
    } catch (err) {
      console.error('Failed to sync CY ECM iframe:', err);
      return null;
    }
  }, []);

  const syncCyAssessmentFromIframe = useCallback(() => {
    const map = collectCyAssessmentFromIframe();
    if (map) {
      cyFlatRef.current = map;
      setValue('cyEcmAssessment_flat', map, { shouldDirty: true });
    }
    return map;
  }, [collectCyAssessmentFromIframe, setValue]);

  const refreshLocalDrafts = useCallback(() => {
    setLocalDrafts(localStorageAPI.getAllDrafts());
  }, []);

  // Auto-save to localStorage on every field change (debounced 1s)
  // Only save when member info has been started (avoid saving a blank form)
  useEffect(() => {
    if (viewState !== 'form') return;
    const data = getValues();
    const hasData = !!(data.memberInfo?.firstName || data.memberInfo?.lastName || data.memberInfo?.medicalId);
    if (!hasData) return;
    const timeout = setTimeout(() => {
      const key = localStorageAPI.saveFormProgress(data, localDraftKey || undefined);
      if (!localDraftKey) setLocalDraftKey(key);
      refreshLocalDrafts();
    }, 1000);
    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, viewState, localDraftKey]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (viewState !== 'form') return;
      const data = getValues();
      const hasData = !!(data.memberInfo?.firstName || data.memberInfo?.lastName || data.memberInfo?.medicalId);
      if (!hasData) return;
      const key = localStorageAPI.saveFormProgress(data, localDraftKey || undefined);
      if (!localDraftKey) setLocalDraftKey(key);
      refreshLocalDrafts();
    }, 30000);
    return () => clearInterval(interval);
  }, [getValues, viewState, localDraftKey, refreshLocalDrafts]);

  // Save to localStorage when the tab/window is closed
  useEffect(() => {
    const handleBeforeUnload = () => {
      const data = getValues();
      const hasData = !!(data.memberInfo?.firstName || data.memberInfo?.lastName || data.memberInfo?.medicalId);
      if (!hasData) return;
      localStorageAPI.saveFormProgress(data, localDraftKey || undefined);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [getValues, localDraftKey]);

  // Check for saved local drafts on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasAccessToken = !!params.get('accessToken');
    const hasEditSubmissionId = !!params.get('editSubmissionId');
    const hasDraftId = !!params.get('draftId');
    const hasCarePlanMode = params.get('carePlanMode') === 'true' && !!params.get('existingSubmissionId');
    if (hasAccessToken || hasEditSubmissionId || hasDraftId || hasCarePlanMode) {
      setShowRestoreDialog(false);
      return;
    }
    const drafts = localStorageAPI.getAllDrafts();
    if (drafts.length > 0) {
      setLocalDrafts(drafts);
    }
  }, [location.search]);

  useEffect(() => {
    if (accessToken || accessMode) {
      setShowRestoreDialog(false);
    }
  }, [accessToken, accessMode]);

  const handleRestoreProgress = useCallback((draft: import('../services/api').LocalDraft) => {
    reset({ ...getDefaultValues(), ...draft.data });
    setLocalDraftKey(draft.draftKey);
    setCurrentStepIndex(0);
    setShowRestoreDialog(false);
    setShowLocalDraftsPanel(false);
  }, [reset]);

  const handleDiscardDraft = useCallback((draftKey: string) => {
    localStorageAPI.clearFormProgress(draftKey);
    const remaining = localStorageAPI.getAllDrafts();
    setLocalDrafts(remaining);
    if (remaining.length === 0) {
      setShowRestoreDialog(false);
      setShowLocalDraftsPanel(false);
    }
  }, []);

  const handleDiscardProgress = useCallback(() => {
    localStorageAPI.getAllDrafts().forEach(d => localStorageAPI.clearFormProgress(d.draftKey));
    setLocalDrafts([]);
    setShowRestoreDialog(false);
  }, []);

  const handleLanguageSelect = useCallback((selectedLanguage: 'en' | 'es') => {
    setLanguage(selectedLanguage);
    setShowLanguagePicker(false);
    // Mark that user has made an explicit language choice
    try {
      window.localStorage.setItem('css_forms_language', selectedLanguage);
      window.localStorage.setItem('css_forms_language_chosen', 'true');
    } catch {
      // localStorage not available, continue anyway
    }
  }, [setLanguage]);

  useEffect(() => {
    if (currentStepIndex > steps.length - 1) {
      setCurrentStepIndex(Math.max(0, steps.length - 1));
    }
  }, [currentStepIndex, steps.length]);

  const safeCurrentStepIndex = useMemo(() => {
    if (steps.length === 0) return 0;
    if (!Number.isFinite(currentStepIndex)) return 0;
    const idx = Math.trunc(currentStepIndex);
    if (idx < 0) return 0;
    if (idx > steps.length - 1) return steps.length - 1;
    return idx;
  }, [currentStepIndex, steps.length]);

  useEffect(() => {
    if (safeCurrentStepIndex !== currentStepIndex) {
      setCurrentStepIndex(safeCurrentStepIndex);
    }
  }, [currentStepIndex, safeCurrentStepIndex]);

  const currentStep =
    steps[safeCurrentStepIndex] ||
    steps[0] || {
      id: 'document-selection',
      title: 'Document Selection',
      description: 'Choose group and individual documents',
      isComplete: false,
    };

  const getStepFromField = useCallback((field: string): number => {
    const rootField = field.split('.')[0];
    const stepId = getRootFieldToStepId(rootField);
    const idx = steps.findIndex((s) => s.id === stepId);
    return idx >= 0 ? idx : 0;
  }, [steps]);

  // Clear errors when step changes
  useEffect(() => {
    setStepErrors([]);
  }, [currentStepIndex]);

  const goToStep = (index: number) => {
    if (index >= 0 && index < steps.length) {
      if (currentStep.id === 'adult-ecm-assessment') {
        syncAdultAssessmentFromIframe();
      }
      if (currentStep.id === 'cy-ecm-assessment') {
        syncCyAssessmentFromIframe();
      }
      // Validate current step before going to a future step
      if (index > currentStepIndex) {
        const data = getValues();
        const validation = validateStep(currentStep.id, data, selectedDocuments);
        if (!validation.isValid) {
          setStepErrors(validation.errors);
          return;
        }
      }
      setCurrentStepIndex(index);
      setStepErrors([]);
      window.scrollTo(0, 0);
    }
  };

  const goNext = () => {
    if (currentStep.id === 'adult-ecm-assessment') {
      syncAdultAssessmentFromIframe();
    }
    if (currentStep.id === 'cy-ecm-assessment') {
      syncCyAssessmentFromIframe();
    }
    // Block if this member already has a submission for one of the selected
    // document types — skip in edit mode. Different document types for the
    // same member are allowed as separate submissions.
    if (currentStep.id === 'member-info' && medicalIdExists && !isEditMode && !accessToken) {
      const conflictLabels = medicalIdConflictingDocuments.map((d) => DOCUMENT_LABELS[d] || d).join(', ');
      setStepErrors([
        conflictLabels
          ? `This member already has a submission for: ${conflictLabels}. Please remove the duplicate document(s) or use a different ID.`
          : 'Medical ID already exists. Please use a different ID.',
      ]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const data = getValues();
    const validation = validateStep(currentStep.id, data, selectedDocuments);

    if (!validation.isValid) {
      setStepErrors(validation.errors);
      // Scroll to error display
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      setStepErrors([]);
      window.scrollTo(0, 0);
    }
  };

  const goPrevious = () => {
    if (currentStep.id === 'adult-ecm-assessment') {
      syncAdultAssessmentFromIframe();
    }
    if (currentStep.id === 'cy-ecm-assessment') {
      syncCyAssessmentFromIframe();
    }
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setStepErrors([]);
      window.scrollTo(0, 0);
    }
  };

  // Navigate to step with error
  const goToErrorStep = (stepIndex: number) => {
    if (!Number.isFinite(stepIndex)) {
      setCurrentStepIndex(0);
      setValidationErrors([]);
      window.scrollTo(0, 0);
      return;
    }
    const normalized = Math.trunc(stepIndex);
    if (normalized < 0 || normalized > steps.length - 1) {
      setCurrentStepIndex(0);
      setValidationErrors([]);
      window.scrollTo(0, 0);
      return;
    }
    setCurrentStepIndex(normalized);
    setValidationErrors([]);
    window.scrollTo(0, 0);
  };

  // Validate all steps before generating PDFs
  const validateAllSteps = (): { isValid: boolean; errors: ValidationError[] } => {
    const data = getValues();
    const allErrors: ValidationError[] = [];

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const validation = validateStep(step.id, data, selectedDocuments, true);
      validation.errors.forEach(err => {
        allErrors.push({
          field: step.id,
          message: err,
          step: i,
        });
      });
    }

    return { isValid: allErrors.length === 0, errors: allErrors };
  };

  // Save as Draft (admin-only)
  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      const data = getValues();
      // Include current language in draft data
      data.language = language;
      const result = await formsAPI.saveDraft(data, safeCurrentStepIndex, draftId || undefined, onBehalfOf || undefined);
      if (result.success) {
        setDraftId(result.draftId);
        // Set submissionId to match draftId so submit will update the same record
        setSubmissionId(result.draftId);
        alert('Draft saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
      saveToLocalOnFailure();
      alert('Failed to save draft.\nYour data has been saved locally.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const canUseDashboardTools = !!getDashboardToken();

  // Check if required member info fields are filled (for enabling Save as Draft)
  const watchedMemberInfo = watch('memberInfo');
  const isMemberInfoComplete = !!(
    watchedMemberInfo?.firstName?.trim() &&
    watchedMemberInfo?.lastName?.trim() &&
    watchedMemberInfo?.medicalId?.trim() &&
    watchedMemberInfo?.address?.street?.trim() &&
    watchedMemberInfo?.address?.city?.trim() &&
    watchedMemberInfo?.address?.state?.trim() &&
    watchedMemberInfo?.address?.zipCode?.trim() &&
    /^\d{5}(-\d{4})?$/.test(watchedMemberInfo?.address?.zipCode || '')
  );

  // Generate PDFs and show preview
  const handleGeneratePDFs = async () => {
    const forcedAdultMap = syncAdultAssessmentFromIframe();
    // Block if this member already has a submission for one of the selected
    // document types — skip in edit mode. Different document types for the
    // same member are allowed as separate submissions.
    if (medicalIdExists && !isEditMode && !accessToken) {
      const memberInfoStep = steps.findIndex((s) => s.id === 'member-info');
      const conflictLabels = medicalIdConflictingDocuments.map((d) => DOCUMENT_LABELS[d] || d).join(', ');
      setValidationErrors([{
        field: 'memberInfo.medicalId',
        message: conflictLabels
          ? `This member already has a submission for: ${conflictLabels}. Please remove the duplicate document(s) or use a different ID.`
          : 'Medical ID already exists in the system',
        step: memberInfoStep >= 0 ? memberInfoStep : 0,
      }]);
      setCurrentStepIndex(memberInfoStep >= 0 ? memberInfoStep : 0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // First validate all steps locally
    const localValidation = validateAllSteps();
    if (!localValidation.isValid) {
      setValidationErrors(localValidation.errors);
      // Go to first step with error
      const firstErrorStep = localValidation.errors[0].step;
      setCurrentStepIndex(firstErrorStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsGenerating(true);
    setGenerateProgress('Validating data...');
    setValidationErrors([]);

    try {
      const data = getValues();
      const adultFlat = data.adultEcmAssessment_flat || {};
      const adultEntries = Object.entries(adultFlat);
      const adultChecked = adultEntries.filter(([, v]) => v === true).length;
      const adultText = adultEntries.filter(([, v]) => typeof v === 'string' && String(v).trim().length > 0).length;
      console.log('Adult ECM flat before generate:', { keys: adultEntries.length, checked: adultChecked, text: adultText });
      console.log('[DEBUG] carePlan.communitySupportsReferral:', data.carePlan?.communitySupportsReferral, typeof data.carePlan?.communitySupportsReferral);
      console.log('[DEBUG] carePlan.dsnpDualEligible:', data.carePlan?.dsnpDualEligible, typeof data.carePlan?.dsnpDualEligible);

      // Build a flattened map of CY ECM template field names so backend/template keys match
      const forcedCyMap = syncCyAssessmentFromIframe();
      const cyFlat = forcedCyMap ?? cyFlatRef.current ?? data.cyEcmAssessment_flat;
      const flatFields = cyFlat && Object.keys(cyFlat).length > 0
        ? cyFlat
        : mapCyEcmAssessmentToFlat(data.cyEcmAssessment);

      setGenerateProgress(t('progress.generatingPdfs'));
      
      // Check if we're in Care Plan mode
      const params = new URLSearchParams(location.search);
      const carePlanMode = params.get('carePlanMode');
      const existingSubmissionId = params.get('existingSubmissionId');
      
      // A stored submission can carry a leftover editContext (a patient
      // access-link token) from whenever it was last saved through that
      // flow. If we blindly spread ...data, that stale token rides along on
      // every future generate/edit — and once the token's own expiry (set
      // when the link was created, e.g. 14 days) passes, every subsequent
      // save for that submission fails with "Invalid or expired access
      // token", even for an admin editing directly. Only a genuinely live
      // accessToken for THIS session should ever be sent.
      const { editContext: _staleEditContext, ...dataWithoutStaleEditContext } = data as FormSubmission & { editContext?: unknown };

      const payload: FormSubmission = {
        ...dataWithoutStaleEditContext,
        language, // Include current language
        cyEcmAssessment_flat: flatFields,
        adultEcmAssessment_flat: forcedAdultMap
          ?? (adultFlatRef.current && Object.keys(adultFlatRef.current).length > 0 ? adultFlatRef.current : null)
          ?? data.adultEcmAssessment_flat ?? {},
        selectedDocuments,
        ...(accessToken ? { editContext: { token: accessToken, mode: accessMode || 'full' } } as any : {}),
        // New-submission "use existing signature" choice: only an intent
        // flag + medicalId are ever sent here — never real signature data.
        // The backend looks up and applies that member's actual signature
        // itself at generation time.
        ...(!isEditMode && reuseSignatureForNewMember && existingMemberMedicalId ? {
          reuseSignatureFrom: { medicalId: existingMemberMedicalId },
        } as any : {}),
        // Care Plan mode handling
        ...(carePlanMode === 'true' && existingSubmissionId ? {
          carePlanMode: true,
          existingSubmissionId: existingSubmissionId,
          selectedDocuments: ['carePlan'] // Only Care Plan in this mode
        } : {}),
      };
      
      // Always set submissionId explicitly from state (for admin edits to reuse existing ID)
      if (submissionId) {
        payload.submissionId = submissionId;
      }
      
      // In Care Plan mode, use the existing submission ID
      if (carePlanMode === 'true' && existingSubmissionId && !submissionId) {
        payload.submissionId = existingSubmissionId;
      }
      const result = await formsAPI.generatePDFs(payload, language);

      if (result.success) {
        setGeneratedPDFs(result.generatedPDFs);
        setSubmissionId(result.submissionId);
        setViewState('preview');
      } else {
        // PDF generation failed — save to localStorage so data isn't lost
        saveToLocalOnFailure();
        // Handle backend validation errors
        if (result.validationErrors && result.validationErrors.length > 0) {
          const mappedErrors: ValidationError[] = result.validationErrors.map((err: any) => ({
            field: err.field,
            message: err.message,
            step: getStepFromField(err.field),
          }));
          setValidationErrors(mappedErrors);

          // Navigate to the first step with an error
          const firstErrorStep = mappedErrors[0].step;
          setCurrentStepIndex(firstErrorStep);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          setValidationErrors([{
            field: 'general',
            message: result.error || 'Failed to generate PDFs',
            step: currentStepIndex,
          }]);
        }
      }
    } catch (err: any) {
      console.error('Generation error:', err);
      // PDF generation threw — save to localStorage so data isn't lost
      saveToLocalOnFailure();

      // 503 = server busy, auto retry with countdown
      if (err.response?.status === 503 && err.response?.data?.queued) {
        const waitSecs = err.response.data.retryAfter || 10;
        let remaining = waitSecs;
        setIsGenerating(true);
        setGenerateProgress(`Server is busy. Retrying in ${remaining}s...`);
        const tick = setInterval(() => {
          remaining -= 1;
          if (remaining <= 0) {
            clearInterval(tick);
            setGenerateProgress('Retrying...');
            handleGeneratePDFs();
          } else {
            setGenerateProgress(`Server is busy. Retrying in ${remaining}s...`);
          }
        }, 1000);
        return;
      }

      // Check if error response has validation errors
      if (err.response?.data?.validationErrors) {
        const mappedErrors: ValidationError[] = err.response.data.validationErrors.map((e: any) => ({
          field: e.field,
          message: e.message,
          step: getStepFromField(e.field),
        }));
        setValidationErrors(mappedErrors);

        // Navigate to the first step with an error
        const firstErrorStep = mappedErrors[0].step;
        setCurrentStepIndex(firstErrorStep);
      } else {
        setValidationErrors([{
          field: 'general',
          message: err.response?.data?.message || err.message || 'Failed to generate PDFs',
          step: currentStepIndex,
        }]);
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsGenerating(false);
      setGenerateProgress('');
    }
  };

  // Fill test data for development
  const handleFillTestData = () => {
    if (localDraftKey) localStorageAPI.clearFormProgress(localDraftKey);
    setLocalDraftKey(null);
    setShowRestoreDialog(false);
    const testData = getTestData();
    reset(testData);
    // Force setValue for nested fields to ensure react-hook-form picks them up
    setValue('documentSelection.selectedGroup', testData.documentSelection!.selectedGroup, { shouldDirty: true });
    setValue('documentSelection.individualDocuments', testData.documentSelection!.individualDocuments, { shouldDirty: true });
    setValue('selectedDocuments', testData.selectedDocuments!, { shouldDirty: true });
    setCurrentStepIndex(0);
    setStepErrors([]);
    setValidationErrors([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Force-save current form data to localStorage (used on any failure)
  const saveToLocalOnFailure = useCallback(() => {
    const data = getValues();
    const hasData = !!(data.memberInfo?.firstName || data.memberInfo?.lastName || data.memberInfo?.medicalId);
    if (!hasData) return;
    const key = localStorageAPI.saveFormProgress(data, localDraftKey || undefined);
    if (!localDraftKey) setLocalDraftKey(key);
    refreshLocalDrafts();
  }, [getValues, localDraftKey, refreshLocalDrafts]);

  // Handle successful submission — remove only this session's local draft
  const handleSubmitSuccess = () => {
    if (localDraftKey) localStorageAPI.clearFormProgress(localDraftKey);
    setLocalDraftKey(null);
    refreshLocalDrafts();
    setViewState('success');
  };

  // Go back to form from preview
  const handleBackToForm = () => {
    setViewState('form');
  };

  // Start new form
  const handleStartNew = () => {
    reset(getDefaultValues());
    setGeneratedPDFs([]);
    setSubmissionId('');
    setCurrentStepIndex(0);
    setViewState('form');
    setStepErrors([]);
    setValidationErrors([]);
    setLocalDraftKey(null);
  };

  // Show PDF Preview
  if (viewState === 'preview') {
    return (
      <PDFPreview
        submissionId={submissionId}
        pdfs={generatedPDFs}
        onSubmitSuccess={handleSubmitSuccess}
        onSubmitFail={saveToLocalOnFailure}
        onBack={handleBackToForm}
        onBehalfOf={onBehalfOf}
      />
    );
  }

  // Show Success Screen
  if (viewState === 'success') {
    return (
      <SubmissionSuccess
        submissionId={submissionId}
        pdfs={generatedPDFs.map(pdf => ({
          formName: pdf.formName,
          downloadUrl: pdf.downloadUrl,
        }))}
        onStartNew={handleStartNew}
      />
    );
  }

  const renderCurrentSection = () => {
    if (!currentStep) return null;
    switch (currentStep.id) {
      case 'document-selection':
        return (
          <>
            {/* Admin: "Who is filling this form?" dropdown */}
            {isAdmin && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="block text-sm font-semibold text-blue-900 mb-2">
                  Who is filling this form?
                </label>
                <p className="text-xs text-blue-700 mb-3">
                  Select the staff member who is working on this form. Their name will appear as "Submitted By" / "Drafted By".
                </p>
                <select
                  className="w-full sm:w-80 border border-blue-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={onBehalfOf ? String(onBehalfOf.userId) : ''}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    if (currentUser && String(currentUser.id) === selectedId) {
                      setOnBehalfOf({
                        userId: currentUser.id,
                        userName: `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email,
                      });
                    } else {
                      const staff = staffUsers.find((u) => String(u.id) === selectedId);
                      if (staff) {
                        setOnBehalfOf({
                          userId: staff.id,
                          userName: `${staff.firstName} ${staff.lastName}`.trim() || staff.email,
                        });
                      }
                    }
                  }}
                  disabled={staffUsersLoading}
                >
                  {/* Admin (self) */}
                  {currentUser && (
                    <option value={String(currentUser.id)}>
                      {`${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email} (You - Admin)
                    </option>
                  )}
                  {/* Staff users */}
                  {staffUsers
                    .filter((u) => String(u.id) !== String(currentUser?.id))
                    .map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {`${u.firstName} ${u.lastName}`.trim() || u.email} (Staff)
                      </option>
                    ))}
                  {staffUsersLoading && <option disabled>Loading staff users...</option>}
                </select>
              </div>
            )}
            <DocumentSelectionSection
              selectedDocuments={selectedDocuments}
            />
          </>
        );
      case 'member-info': {
        const params = new URLSearchParams(location.search);
        const isCarePlanExistingMode = params.get('carePlanMode') === 'true' && !!params.get('existingSubmissionId');
        return (
          <MemberInfoSection
            onMedicalIdStatusChange={handleMedicalIdStatusChange}
            onFoundExistingMemberChange={handleFoundExistingMemberChange}
            isEditMode={isEditMode}
            isPatientAccess={!!accessToken}
            isLocked={isCarePlanExistingMode}
            selectedDocuments={selectedDocuments}
          />
        );
      }
      case 'roi':
        return <ROISection />;
      case 'housing':
        return <HousingSection />;
      case 'consent':
        return <ConsentSection />;
      case 'care-plan':
        return <CarePlanSection />;
      case 'partnership':
        return <PartnershipSection />;
      case 'partnership-care-plan':
        return <PartnershipCarePlanSection />;
      case 'ihsp':
        return <IHSPSection />;
      case 'php-cs-referral':
        return <PhpCsReferralSection />;
      case 'ecm-referral-child-youth':
        return <EcmReferralChildYouthSection />;
      case 'ecm-referral-adult':
        return <EcmReferralAdultSection />;
      case 'cchp-ecm-referral-child-youth':
        return <CchpEcmReferralChildYouthSection />;
      case 'cchp-ecm-referral-adult':
        return <CchpEcmReferralAdultSection />;
      case 'cy-ecm-assessment':
        return <CYECMAssessmentSection />;
      case 'adult-ecm-assessment':
        return <AdultECMAssessmentSection />;
      case 'signature':
        return (
          <SignatureSection
            onGenerate={handleGeneratePDFs}
            isGenerating={isGenerating}
            offerReuseExistingSignature={hasNewlyAddedDocuments}
            existingSignature={originalSignature}
            offerReuseForNewSubmission={!isEditMode && foundExistingMember && existingMemberHasSignature}
            onReuseSignatureChoice={handleReuseSignatureChoice}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">

        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          link={typeof window !== 'undefined' ? window.location.href : ''}
        />

        {/* Language Picker Modal */}
        {showLanguagePicker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="text-center mb-6">
                <div className="mb-4">
                  <img src="/css_logo.png" alt="California Support Services" className="h-12 mx-auto" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {t('languagePicker.welcome')}
                </h2>
                <p className="text-gray-600">
                  {t('languagePicker.chooseLanguage')}
                </p>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={() => handleLanguageSelect('en')}
                  className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="font-semibold text-gray-900">{t('languagePicker.english')}</div>
                </button>
                <button
                  onClick={() => handleLanguageSelect('es')}
                  className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="font-semibold text-gray-900">{t('languagePicker.spanish')}</div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('wizard.title')}</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                {t('wizard.subtitle')}
              </p>
            </div>
            {/*
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Clear all saved progress and start fresh?')) {
                    localStorageAPI.clearFormProgress();
                    reset(getDefaultValues());
                    setCurrentStepIndex(0);
                    window.location.reload();
                  }
                }}
                className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
                title="Clear all data and start fresh"
              >
                Clear & Reset
              </button>
              <button
                type="button"
                onClick={() => setIsShareModalOpen(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center gap-2 text-sm font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </button>
            </div>
            */}
          </div>
        </div>
        {/* <div className="mb-4">
          <button
            type="button"
            onClick={handleFillTestData}
            className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 text-sm font-medium"
            title="Fill all forms with test data for development"
          >
            Fill Test Data
          </button>
        </div> */}


        {/* Validation Errors from Backend */}
        {
          validationErrors.length > 0 && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm sm:text-base font-medium text-red-800 mb-2">{t('errors.fixFollowing')}</h4>
                  <ul className="space-y-2">
                    {validationErrors.map((error, index) => (
                      <li key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm gap-2">
                        <span className="text-red-700">
                          <strong>{steps[error.step]?.title || t('errors.form')}:</strong> {error.message}
                        </span>
                        <button
                          onClick={() => goToErrorStep(error.step)}
                          className="text-red-600 hover:text-red-800 font-medium underline text-left sm:text-right sm:ml-4 flex-shrink-0"
                        >
                          {t('button.goToField')}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <button
                onClick={() => setValidationErrors([])}
                className="mt-3 text-sm text-red-600 hover:text-red-800"
              >
                {t('button.dismiss')}
              </button>
            </div>
          )
        }

        {/* Step Validation Errors */}
        {
          stepErrors.length > 0 && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm sm:text-base font-medium text-amber-800 mb-2">{t('errors.completeFollowing')}</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {stepErrors.map((error, index) => (
                      <li key={index} className="text-sm text-amber-700">{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )
        }

        {/* Progress Bar */}
        <ProgressBar steps={steps} currentStepIndex={safeCurrentStepIndex} onStepClick={goToStep} />

        {/* Current Step Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 sm:mb-6">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {t('wizard.stepHeading', {
                current: safeCurrentStepIndex + 1,
                title: STEP_TITLE_KEY[currentStep.id] ? t(STEP_TITLE_KEY[currentStep.id]) : currentStep.title,
              })}
            </h2>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              {STEP_DESC_KEY[currentStep.id] ? t(STEP_DESC_KEY[currentStep.id]) : currentStep.description}
            </p>
          </div>

          {/* Form Content */}
          <div className="p-4 sm:p-6">{renderCurrentSection()}</div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={goPrevious}
            disabled={safeCurrentStepIndex === 0}
            className={`w-full sm:w-auto px-6 py-2 rounded-lg font-medium text-sm sm:text-base ${safeCurrentStepIndex === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
          >
            {t('button.previous')}
          </button>

            <div className="flex items-center gap-3 order-first sm:order-none">
            <span className="text-xs sm:text-sm text-gray-500">
              {t('wizard.stepOf', { current: safeCurrentStepIndex + 1, total: steps.length })}
            </span>
            {safeCurrentStepIndex >= 1 && !isEditMode && !accessToken && (
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSavingDraft || !isMemberInfoComplete}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition flex items-center gap-2 ${isMemberInfoComplete ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                title={!isMemberInfoComplete ? t('draft.saveFirst') : t('draft.saveTooltip')}
              >
                {isSavingDraft ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('button.saving')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    {t('button.saveDraft')}
                  </>
                )}
              </button>
            )}
          </div>

          <div className="w-full sm:w-auto">
            {currentStep.id === 'signature' ? (
              <button
                type="button"
                onClick={handleGeneratePDFs}
                disabled={isGenerating}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2 rounded-lg font-medium text-white text-sm sm:text-base flex items-center justify-center gap-2 ${isGenerating
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {isGenerating ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="hidden sm:inline">{generateProgress}</span>
                    <span className="sm:hidden">{t('button.generating')}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="hidden sm:inline">{t('button.generatePdfs', { count: selectedDocuments.length })}</span>
                    <span className="sm:hidden">{t('button.generatePdfsShort')}</span>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                className="w-full sm:w-auto px-6 py-2 rounded-lg font-medium text-sm sm:text-base bg-blue-600 text-white hover:bg-blue-700"
              >
                {t('button.next')}
              </button>
            )}
          </div>
        </div>

        {/* Auto-save indicator */}
        <div className="mt-4 text-center text-xs text-gray-400">
          {t('wizard.autoSaveNotice')}
        </div>
      </div>

      {/* Floating Local Drafts Button — always visible */}
      <button
        onClick={() => setShowLocalDraftsPanel(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-semibold px-3 py-2 rounded-full shadow-lg transition-all"
        title="View locally saved drafts"
      >
        💾
        <span>Local Drafts</span>
        {localDrafts.length > 0 && (
          <span className="bg-white text-yellow-600 font-bold rounded-full w-5 h-5 flex items-center justify-center text-xs">
            {localDrafts.length}
          </span>
        )}
      </button>

      {/* Local Drafts Panel */}
      {showLocalDraftsPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-lg w-full shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-yellow-500 text-xl">💾</span>
                <h3 className="text-base sm:text-lg font-semibold">Local Drafts ({localDrafts.length})</h3>
              </div>
              <button onClick={() => setShowLocalDraftsPanel(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            </div>
            <p className="text-xs text-gray-400 mb-3">Auto-saved drafts on this device. Kept for 7 days. Removed on successful submission.</p>

            <div className="space-y-2 max-h-72 overflow-y-auto">
              {localDrafts.map((draft) => {
                const firstName = (draft.data as any)?.memberInfo?.firstName || '';
                const lastName = (draft.data as any)?.memberInfo?.lastName || '';
                const name = (firstName + ' ' + lastName).trim() || 'Unnamed';
                const savedAt = new Date(draft.savedAt).toLocaleString();
                const isActive = draft.draftKey === localDraftKey;
                return (
                  <div key={draft.draftKey} className={`flex items-center justify-between border rounded-lg px-3 py-2 ${isActive ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {name}
                        {isActive && <span className="ml-2 text-xs text-blue-600 font-normal">(current)</span>}
                      </p>
                      <p className="text-xs text-gray-400">Saved: {savedAt}</p>
                    </div>
                    <div className="flex gap-2 ml-3">
                      {!isActive && (
                        <button
                          onClick={() => { handleRestoreProgress(draft); setShowLocalDraftsPanel(false); }}
                          className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 whitespace-nowrap"
                        >
                          Restore
                        </button>
                      )}
                      <button
                        onClick={() => handleDiscardDraft(draft.draftKey)}
                        className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded hover:bg-red-200"
                        title="Delete this draft"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => setShowLocalDraftsPanel(false)}
              className="mt-4 w-full text-sm text-gray-500 py-2 px-4 rounded border border-gray-200 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </FormProvider>
  );
};

export default FormWizard;
